import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { useCallback } from 'react';

import { company } from '~/company/Company';
import { formatVolume } from '~/components/ProductCard/utils';
import { useProductCardV3 } from '~/components/ProductCardV3/hooks/useProductCardV3';
import {
  AddToCartSection,
  ProductBadges,
  ProductImage,
  ProductInfo,
} from '~/components/ProductCardV3/shared';
import type { ProductCardVariantProps } from '~/components/ProductCardV3/types';
import { useCategoryAnalytics } from '~/hooks/useCategoryAnalytics';

import styles from './ProductCardM.module.scss';

/**
 * ProductCardM - Medium size product card (179px mobile, 227px web)
 * Card shows only main offer (no SKU selection)
 */
const ProductCardM = ({
  product,
  className,
  showColorVariants = false,
  hideButton = false,
  productState,
  disabled = false,
}: ProductCardVariantProps) => {
  const { handleClickProduct } = useCategoryAnalytics();

  const {
    currentItem,
    limit,
    image,
    oldPrice,
    currentPrice,
    totalCartPrice,
    cartItemsCount,
    isFavorite,
    isOutOfStock,
    colorVariants,
    badges,
    multiBuyInfo,
    productHasManyVariants,
    isBundle,
    selectOffer,
    toggleFavorite,
    changeCount,
    openSelectSKUModal,
  } = useProductCardV3(
    productState?.data ?? product,
    productState?.selectedOffer,
    'm',
  );

  const discountBadge = badges.find((badge) => badge.type === 'discount');

  const visibleBadges = company.match({
    mcosmetic: discountBadge ? [discountBadge] : [],
    default: badges,
  });

  const handleProductClick = useCallback(() => {
    handleClickProduct(product);
  }, [product, handleClickProduct]);

  const volume = formatVolume(currentItem.properties);
  const hasDiscount = oldPrice && oldPrice !== currentPrice;

  return (
    <div
      className={classes([styles.card, disabled && styles.disabled, className])}
      data-company={company.name}
    >
      <ProductImage
        src={image}
        alt={currentItem.name}
        size="m"
        badges={
          visibleBadges.length ? (
            <ProductBadges
              variant={company.match({
                mcosmetic: 'brandOutline',
                default: 'primary',
              })}
              badges={visibleBadges}
              size="s"
            />
          ) : null
        }
        isFavorite={isFavorite}
        onToggleFavorite={toggleFavorite}
        productSlug={product.slug}
        offerSku={currentItem.sku ?? ''}
        onProductClick={handleProductClick}
        cartItemsCount={cartItemsCount}
        currentPrice={currentPrice}
        totalCartPrice={totalCartPrice}
        isOutOfStock={isOutOfStock}
      />

      <div className={styles.infoWrapper}>
        <ProductInfo
          name={currentItem.name}
          volume={volume}
          rating={currentItem.ratingAverage}
          reviewsCount={currentItem.ratingMarksCount}
          isRatingShown={false}
          size="m"
          colorVariants={
            showColorVariants && colorVariants.length > 1
              ? colorVariants.map((v) => ({
                  color: v.value,
                  sku: v.sku,
                  name: v.value,
                }))
              : undefined
          }
          onVariantSelect={selectOffer}
          productSlug={product.slug}
          currentSku={currentItem.sku ?? ''}
          multiBuyInfo={multiBuyInfo}
          cartItemsCount={cartItemsCount}
          promoRequiredQuantity={currentItem.getLowestMultibuyLevel()?.quantity}
        />

        {!hideButton && (
          <AddToCartSection
            currentPrice={currentPrice}
            oldPrice={hasDiscount ? oldPrice : undefined}
            cartItemsCount={cartItemsCount}
            limit={limit}
            size="m"
            onChangeCount={changeCount}
            productId={productState?.data?.id ?? product?.id ?? 0}
            sku={productState?.selectedOffer?.sku ?? currentItem.sku ?? ''}
            isOutOfStock={isOutOfStock}
            productSlug={product.slug}
            productHasManyVariants={productHasManyVariants}
            isBundle={isBundle}
            onOpenSelectSKUModal={openSelectSKUModal}
          />
        )}
      </div>

      {disabled && <div className={styles.disabledOverlay} />}
    </div>
  );
};

export default observer(ProductCardM);
